import { v4 as uuidv4 } from 'uuid';

export interface Context {
  userID?: string;
  clientSideUserId?: string;
  _pcid?: string;
}

export interface ProviderContext {
  correlation_id: string;
  visitorId: string;
  [key: string]: any;
}

export interface TrackingContext {
  provider_name: string;
  provider_context: ProviderContext;
}

export interface TrackingData {
  name?: string;
  data: {
    url_from?: string;
    url_to?: string | null;
    label?: string;
    [key: string]: any;
  };
}

export interface Tracker {
  variables: {
    correlationId: string | undefined;
    visitorId: string | undefined;
  };
  constants: {
    trackingApiEndpoint: string;
    trackingApiConfigurationEndpoint: string;
    types: string[];
  };
  elements: {
    links: NodeListOf<HTMLAnchorElement>;
    buttons: NodeListOf<HTMLButtonElement>;
  };
  methods: {
    initialize: () => void;
    checkIsEnabled: () => Promise<Record<string, string>>;
    sendDataToApi: (trackingData: TrackingData) => void;
    pageLoadEvent: () => void;
    getOrCreateUserId: () => void;
    getOrCreateContext: () => void;
    getButtonActionUrl: (button: HTMLButtonElement) => string | null;
    getInputContext: (input: HTMLInputElement) => string | null;
  };
  events: {
    linksTracking: () => void;
    buttonsTracking: () => void;
    inputTracking: () => void;
  };
}

(() => {
  const tracker: Tracker = {
    variables: {
      correlationId: undefined,
      visitorId: undefined,
    },
    constants: {
      trackingApiEndpoint: '',
      trackingApiConfigurationEndpoint: window.location.origin.includes('ovhcloud.com')
        ? 'https://www.ovhcloud.com/website/session_handler/config/website_session.json'
        : 'https://ovhcloudcom.static.ovh.net/website/session_handler/config/website_session-preprod.json',
      types: ['checkbox', 'radio', 'submit', 'search', 'button'],
    },
    elements: {
      links: document.querySelectorAll('a'),
      buttons: document.querySelectorAll('button, osds-button'),
    },
    methods: {
      initialize: () => {
        tracker.methods.checkIsEnabled().then((data) => {
          if (data.enabled) {
            tracker.constants.trackingApiEndpoint = data.trackingApiEndpoint;
            tracker.methods.getOrCreateContext();
            tracker.methods.pageLoadEvent();
            tracker.events.linksTracking();
            tracker.events.buttonsTracking();
            tracker.events.inputTracking();
          }
        });
      },
      getButtonActionUrl: (button) => {
        const form = button.closest('form');
        return form ? form.getAttribute('action') : null;
      },
      checkIsEnabled: () => {
        const { trackingApiConfigurationEndpoint } = tracker.constants;
        return fetch(trackingApiConfigurationEndpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })
          .then((response) => response.json())
          .then((data) => {
            return data;
          })
          .catch((error) => {
            console.error('Error:', error);
          });
      },
      getInputContext: (input) => {
        const form = input.closest('input');
        return form ? form.getAttribute('id') || form.getAttribute('action') : null;
      },
      pageLoadEvent: () => {
        const trackingData = {
          name: 'page.display',
          data: {
            source_url: window.location.href,
            url_from: document.referrer,
            url_to: window.location.href,
          },
        };
        tracker.methods.sendDataToApi(trackingData);
      },
      getOrCreateUserId: () => {
        const cookieName = 'userID';
        const match = document.cookie.match(new RegExp('(^| )' + cookieName + '=([^;]+)'));
        if (match) {
          tracker.variables.correlationId = match[2];
        } else {
          const newId = uuidv4();
          document.cookie = `${cookieName}=${newId}; path=/; max-age=${
            60 * 60 * 24 * 365
          }; SameSite=Lax`;

          tracker.variables.correlationId = newId;
        }
      },
      getOrCreateContext: () => {
        const ctx: Context = document.cookie
          .split(';')
          .map((v: string): string[] => v.split('='))
          .reduce(
            (acc, v) => {
              const key = decodeURIComponent(v[0].trim());
              const value = v.length > 1 ? decodeURIComponent(v.slice(1).join('=').trim()) : '';
              acc[key] = value;
              return acc;
            },
            {} as Record<string, string>,
          );

        if (ctx.hasOwnProperty('userID')) {
          tracker.variables.correlationId = ctx.userID;
        } else {
          const newId = uuidv4();
          document.cookie = `userID=${newId}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;

          tracker.variables.correlationId = newId;
        }

        if (ctx.hasOwnProperty('clientSideUserId')) {
          tracker.variables.visitorId = ctx.clientSideUserId;
          return;
        }

        if (ctx.hasOwnProperty('_pcid')) {
          try {
            const val = JSON.parse(ctx._pcid!);
            tracker.variables.visitorId = val.browserId;
          } catch (err) {
            // silently die
          }
          return;
        }
        const newClientId = uuidv4();
        document.cookie = `clientSideUserId=${newClientId}; path=/; max-age=${
          60 * 60 * 24 * 365
        }; SameSite=Lax`;

        tracker.variables.visitorId = newClientId;
      },
      sendDataToApi: (trackingData: TrackingData) => {
        const { trackingApiEndpoint } = tracker.constants;
        fetch(trackingApiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            provider_name: 'analytics',
            provider_context: {
              correlation_id: tracker.variables.correlationId,
              visitor_id: tracker.variables.visitorId,
            },
            events: [trackingData],
          }),
        })
          .then((response) => response.json())
          .catch((error) => {
            console.error('Error:', error);
          });
      },
    },
    events: {
      linksTracking: () => {
        tracker.elements.links.forEach((link) => {
          link.addEventListener('click', () => {
            const trackingData: TrackingData = {
              name: 'click.navigation',
              data: {
                source_url: window.location.href,
                url_from: window.location.href,
                url_to: link.getAttribute('href'),
                label: link.innerText,
              },
            };
            tracker.methods.sendDataToApi(trackingData);
          });
        });
      },
      buttonsTracking: () => {
        tracker.elements.buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const to = tracker.methods.getButtonActionUrl(button);
            const trackingData: TrackingData = {
              name: 'click.action.submit',
              data: {
                source_url: window.location.href,
                url_from: window.location.href,
                url_to: to,
                label: button.innerText,
              },
            };
            tracker.methods.sendDataToApi(trackingData);
          });
        });
      },
      inputTracking: () => {
        const { types } = tracker.constants;
        const selector = types.map((type) => `input[type="${type}"]`).join(', ');
        const inputs = document.querySelectorAll<HTMLInputElement>(selector);
        const { sendDataToApi } = tracker.methods;
        inputs.forEach((input) => {
          const context = tracker.methods.getInputContext(input);
          input.addEventListener('change', () => {
            const data: TrackingData = {
              name: 'click.action.form',
              data: {
                type: input.getAttribute('type'),
                value: `${input.getAttribute('name')}::${input.value}`,
                context,
              },
            };
            sendDataToApi(data);
          });
        });
      },
    },
  };
  tracker.methods.initialize();
})();
